import _get from 'lodash/get'

import type { FlowValues } from '@flows/core/types/stepTypes'

type ResolvableOption = { readonly value: string | boolean | number }

type OptionsElement<TOption extends ResolvableOption> = {
  readonly options: readonly unknown[] | string
  readonly optionsTransformer?: (values: readonly any[]) => TOption[]
  readonly filterOptions?: (options: TOption[], flowValues: FlowValues) => TOption[]
  readonly exclusiveValues?: readonly TOption['value'][]
}

const getOptionsSource = (
  options: OptionsElement<ResolvableOption>['options'],
  flowValues: FlowValues
) => (typeof options === 'string' ? _get(flowValues, options) : options)

const transformOptions = <TOption extends ResolvableOption>(
  source: unknown,
  optionsTransformer?: OptionsElement<TOption>['optionsTransformer']
): TOption[] => {
  const copied = (Array.isArray(source) ? [...source] : []) as TOption[]
  if (!optionsTransformer) {
    return copied
  }
  try {
    return optionsTransformer(copied)
  } catch (error) {
    console.error('optionsTransformer failed, showing no options', error)
    return []
  }
}

const filterElementOptions = <TOption extends ResolvableOption>(
  options: TOption[],
  { filterOptions, exclusiveValues }: Omit<OptionsElement<TOption>, 'options'>,
  flowValues: FlowValues
): TOption[] => {
  if (!filterOptions) {
    return options
  }
  try {
    const keptValues = new Set(
      filterOptions([...options], flowValues).map((option) => option.value)
    )
    return options.filter(
      (option) => keptValues.has(option.value) || exclusiveValues?.includes(option.value)
    )
  } catch (error) {
    console.error('filterOptions failed, showing the unfiltered options', error)
    return options
  }
}

export const resolveElementOptions = <TOption extends ResolvableOption>(
  element: OptionsElement<TOption>,
  flowValues: FlowValues
): TOption[] =>
  filterElementOptions(
    transformOptions(getOptionsSource(element.options, flowValues), element.optionsTransformer),
    element,
    flowValues
  )
